import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText, SelectChangeEvent } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { updateTicketResolution } from '../../../../state';
import { useGetResolutionCategoriesQuery } from '../../../../../../services/DimensionService/dimensionApi';

/**
 * ResolutionCategorySelect - Self-contained field editor for ticket resolution category
 * Automatically filters resolution categories based on the selected ticket category
 * Disables when no ticket category is selected
 */
const ResolutionCategorySelect: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentTicket } = useAppSelector(state => state.ticket);

  const ticketCategory = currentTicket?.identification?.category || '';
  const resolutionCategory = currentTicket?.resolution?.category || '';

  // Fetch resolution categories filtered by ticket category
  const { 
    data: categories = [], 
    isLoading,
    isError 
  } = useGetResolutionCategoriesQuery(ticketCategory, {
    skip: !ticketCategory, // Don't fetch if no ticket category is selected
  });

  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    
    if (currentTicket?.resolution) {
      dispatch(updateTicketResolution({
        ...currentTicket.resolution,
        category: value || undefined,
      }));
    }
  };

  // Check if the current value is valid in the available options
  const isValidCategory = categories.some(cat => cat.value === resolutionCategory);
  const selectValue = isValidCategory || isLoading ? resolutionCategory : '';

  const isDisabled = !ticketCategory || isLoading;

  return (
    <FormControl 
      size="small" 
      fullWidth
      disabled={isDisabled}
      error={isError}
    >
      <InputLabel>Resolution Category</InputLabel>
      <Select
        value={selectValue}
        label="Resolution Category"
        onChange={handleChange}
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {categories.map((category) => (
          <MenuItem 
            key={category.value} 
            value={category.value}
            title={category.description}
          >
            {category.name}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {!ticketCategory 
          ? 'Select a ticket category first' 
          : isError 
          ? 'Error loading resolution categories'
          : 'Select the resolution category that best describes the outcome'
        }
      </FormHelperText>
    </FormControl>
  );
};

export default ResolutionCategorySelect;
